/* prettier-ignore */

import { effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { DataChatService } from './dataChat.service';

const SILENT_WAV_DATA_URI = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQAAAAA=';

@Injectable({ providedIn: 'root' })
export class PlayAudioHandlerService {
  private readonly ngZone = inject(NgZone);
  private dataChat = inject(DataChatService);

  public isPlaying = signal(false);
  public isPlayingOnDemand = signal<string | null>(null);

  private externalAudio: HTMLAudioElement | null = null;
  private externalBlobUrl: string | null = null;
  private onDemandAudio: HTMLAudioElement | null = null;
  private onDemandBlobUrl: string | null = null;
  private audioUnlocked = false;
  private unlockInFlight = false;
  private externalFetchController: AbortController | null = null;

  listenAbortEffect = effect((onCleanup) => {
      const controller = this.dataChat.abortSignal();
      const onAbort = () => {
          this.stopNow();
      };

      controller.signal.addEventListener('abort', onAbort);
      onCleanup(() => {
          controller.signal.removeEventListener('abort', onAbort);
      });
  });

  async stopNow(): Promise<void> {
    this.releaseExternalAudio();
    this.isPlaying.set(false);
  }

  public unlockAudioFromGesture(): void {
    if (this.audioUnlocked || this.unlockInFlight) 
      return;

    this.unlockInFlight = true;
    const audio = this.getOrCreateExternalAudio();
    audio.muted = true;
    audio.volume = 0;
    audio.src = SILENT_WAV_DATA_URI;

    const unlockPromise = audio.play();
    if (!unlockPromise || typeof unlockPromise.then !== 'function') {
      this.unlockInFlight = false;
      audio.muted = false;
      audio.volume = 1;
      return;
    }

    unlockPromise
      .then(() => {
        this.audioUnlocked = true;
      })
      .catch((err) => {
        console.warn('[PlayAudioHandler] iOS audio unlock blocked:', err);
      })
      .finally(() => {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch {}
        audio.muted = false;
        audio.volume = 1;
        this.unlockInFlight = false;
      });
  }

  public playExternalAudio(url: string) {
    const audio = this.getOrCreateExternalAudio();
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {}

    this.abortExternalFetch();
    this.revokeExternalBlobUrl();

    const controller = new AbortController();
    this.externalFetchController = controller;

    this.ngZone.runOutsideAngular(() => {
      fetch(url, { signal: controller.signal })
        .then(res => {
          if (!res.ok) 
            throw new Error(`HTTP ${res.status}`);

          return res.blob();
        })
        .then(blob => {
          if (controller.signal.aborted) 
            return;

          this.externalBlobUrl = URL.createObjectURL(blob);
          audio.src = this.externalBlobUrl;

          let finished = false;

          const cleanupAudioHandlers = () => {
            audio.onplaying = null;
            audio.onended = null;
            audio.onerror = null;
          };

          const onPlaying = () => this.ngZone.run(() => this.isPlaying.set(true));
          const onDone = () => {
            if (finished) 
              return;

            finished = true;
            cleanupAudioHandlers();
            this.revokeExternalBlobUrl();
            this.ngZone.run(() => this.isPlaying.set(false));
          };

          audio.onplaying = onPlaying;
          audio.onended = onDone;
          audio.onerror = (e) => {
            console.warn('[PlayAudioHandler] Audio element error:', e);
            onDone();
          };

          return audio.play().catch(err => {
            onDone();
            throw err;
          });
        })
        .catch(err => {
          if (err?.name === 'AbortError') 
            return;
          
          console.warn('[PlayAudioHandler] Error playing external audio:', err);
          this.revokeExternalBlobUrl();
          this.ngZone.run(() => this.isPlaying.set(false));
        });
    });
  }

  /**
   * Call from a user gesture (click/tap) BEFORE any async work,
   * so iOS Safari blesses the on-demand audio element.
   */
  public prepareOnDemandFromGesture(): void {
    const audio = this.getOrCreateOnDemandAudio();
    audio.muted = true;
    audio.volume = 0;
    audio.src = SILENT_WAV_DATA_URI;

    const p = audio.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        try { audio.pause(); audio.currentTime = 0; } catch {}
      }).catch(() => {}).finally(() => {
        audio.muted = false;
        audio.volume = 1;
      });
    } else {
      audio.muted = false;
      audio.volume = 1;
    }
  }

  public async playAudioOnDemand(audioObject: { completion_id: string; audioBlob: Blob }) {
    this.stopAudioOnDemand();

    const { completion_id, audioBlob } = audioObject;
    this.onDemandBlobUrl = URL.createObjectURL(audioBlob);

    this.ngZone.runOutsideAngular(() => {
      const audio = this.getOrCreateOnDemandAudio();
      audio.onended = null;
      audio.onerror = null;
      audio.src = this.onDemandBlobUrl!;
      audio.load();

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(err => {
          console.warn('[PlayAudioHandler] Error playing on-demand audio:', err);
          this.revokeOnDemandBlobUrl();
          this.ngZone.run(() => this.isPlayingOnDemand.set(null));
        });
      }
      audio.onended = () => {
        this.revokeOnDemandBlobUrl();
        this.ngZone.run(() => this.isPlayingOnDemand.set(null));
      };
    });

    this.isPlayingOnDemand.set(completion_id);
  }

  public stopAudioOnDemand() {
    if (this.onDemandAudio) {
      try { this.onDemandAudio.pause(); } catch {}
      this.onDemandAudio.onended = null;
      this.onDemandAudio.onerror = null;
    }
    this.revokeOnDemandBlobUrl();
    this.isPlayingOnDemand.set(null);
  }

  private revokeOnDemandBlobUrl(): void {
    if (this.onDemandBlobUrl) {
      URL.revokeObjectURL(this.onDemandBlobUrl);
      this.onDemandBlobUrl = null;
    }
  }

  private releaseExternalAudio(): void {
    this.abortExternalFetch();
    if (!this.externalAudio) return;
    try {
      this.externalAudio.pause();
      this.externalAudio.onended = null;
      this.externalAudio.onerror = null;
      this.externalAudio.currentTime = 0;
    } catch {}
    this.revokeExternalBlobUrl();
  }

  private abortExternalFetch(): void {
    if (this.externalFetchController) {
      this.externalFetchController.abort();
      this.externalFetchController = null;
    }
  }

  private revokeExternalBlobUrl(): void {
    if (this.externalBlobUrl) {
      URL.revokeObjectURL(this.externalBlobUrl);
      this.externalBlobUrl = null;
    }
  }

  private getOrCreateExternalAudio(): HTMLAudioElement {
    if (!this.externalAudio) {
      this.externalAudio = new Audio();
      this.configureAudioForIOS(this.externalAudio);
    }
    return this.externalAudio;
  }

  private getOrCreateOnDemandAudio(): HTMLAudioElement {
    if (!this.onDemandAudio) {
      this.onDemandAudio = new Audio();
      this.configureAudioForIOS(this.onDemandAudio);
    }
    return this.onDemandAudio;
  }

  private configureAudioForIOS(audio: HTMLAudioElement): void {
    audio.preload = 'auto';
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
  }

}
